import { useState } from "react";
import { OctagonX, ShieldCheck } from "lucide-react";

import { api, ApiError } from "@/api/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface EmergencyStopControlProps {
  /** Current kill-switch state from /api/system/state. null while unknown. */
  active: boolean | null | undefined;
  /** Called after a successful engage/clear so the parent can re-poll. */
  onChanged?: () => void;
  /** Header placement: an icon-only kill-switch on mobile (a compact octagon-X that
   *  sits inline right after the price), expanding to a larger labelled centre-stage
   *  button at sm+. The confirm dialog is identical in both. */
  prominent?: boolean;
}

/**
 * F3 — manual emergency-stop kill-switch. A GUARDED system control (SPEC
 * §19): it halts automated trading, it is NOT an IG order, and it never
 * fires automatically — every action passes through an explicit confirm
 * dialog. Engaged state renders in dominant red (SPEC §14).
 */
export default function EmergencyStopControl({
  active,
  onChanged,
  prominent = false,
}: EmergencyStopControlProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isActive = active === true;

  const run = async () => {
    setPending(true);
    setError(null);
    try {
      if (isActive) {
        await api.clearEmergencyStop();
      } else {
        await api.activateEmergencyStop();
      }
      setOpen(false);
      onChanged?.();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `Refusé (HTTP ${e.status}).`
          : "Requête échouée. L'état du coupe-circuit est inchangé.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      {isActive ? (
        <button
          type="button"
          onClick={() => {
            setError(null);
            setOpen(true);
          }}
          className={cn(
            "bg-live inline-flex animate-pulse items-center justify-center gap-1.5 rounded-md font-bold tracking-wide text-white uppercase shadow-sm hover:brightness-110",
            prominent
              ? "size-9 sm:h-auto sm:w-auto sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
              : "px-2.5 py-1.5 text-xs",
          )}
          aria-label="Arrêt d'urgence enclenché — cliquer pour lever"
          title="Arrêt d'urgence enclenché — toucher pour lever"
          data-testid="emergency-stop-control"
          data-active="true"
        >
          <OctagonX className={prominent ? "size-5" : "size-4"} aria-hidden />
          <span className={cn(prominent && "hidden sm:inline")}>Stop enclenché</span>
        </button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size={prominent ? "icon" : "sm"}
          onClick={() => {
            setError(null);
            setOpen(true);
          }}
          className={cn(
            "border-live/50 text-live hover:bg-live/10 hover:text-live",
            prominent &&
              "sm:h-10 sm:w-auto sm:gap-2 sm:px-4 sm:text-sm sm:font-semibold sm:shadow-sm",
          )}
          aria-label="Enclencher l'arrêt d'urgence"
          title="Enclencher l'arrêt d'urgence"
          data-testid="emergency-stop-control"
          data-active="false"
        >
          <OctagonX className={prominent ? "size-5" : "size-4"} aria-hidden />
          <span className="hidden sm:inline">Arrêt d'urgence</span>
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isActive ? (
                <ShieldCheck className="text-buy size-5" aria-hidden />
              ) : (
                <OctagonX className="text-live size-5" aria-hidden />
              )}
              {isActive ? "Lever l'arrêt d'urgence ?" : "Enclencher l'arrêt d'urgence ?"}
            </DialogTitle>
            <DialogDescription>
              {isActive
                ? "Lève le coupe-circuit manuel : le bot peut rouvrir des cycles. Confirme seulement si tu veux réactiver le trading automatique."
                : "Stoppe le trading automatique immédiatement. Geste système de protection, pas un ordre IG — mais attention : tant qu'il est enclenché, le bot FERME chaque position ouverte au tick suivant (et n'en ouvre aucune). Enclenche seulement si tu veux tout aplatir et arrêter."}
            </DialogDescription>
          </DialogHeader>
          {error && (
            <p className="text-loss text-sm" role="alert">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={pending}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant={isActive ? "default" : "destructive"}
              onClick={run}
              disabled={pending}
              data-testid="emergency-stop-confirm"
              className={cn(isActive && "bg-buy text-white hover:bg-buy/90")}
            >
              {pending
                ? "En cours…"
                : isActive
                  ? "Lever le stop"
                  : "Enclencher l'arrêt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
